"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef, useState } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import Link from "next/link";
import { Loader2, AlertCircle, CheckCircle2, KeyRound, Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { isGoogleMailAddress } from "@/lib/auth-google-email";
import { CONTACT_EMAIL } from "@/lib/site";
import { buildPasswordRecoveryRedirectTo, getFirstPasswordIssue, getPasswordStrength, type PasswordStrengthIssue } from "@/lib/auth-recovery";
import { isLikelyExistingAccountSignUp } from "@/lib/auth-signup-result";
import { isUserBannedAuthError } from "@/lib/ban-details";
import { normalizeInternalPath } from "@/lib/redirects";
import { createClient, isSupabaseConfigured } from "@/lib/supabase/client";
import { REFERRAL_REWARD_DAYS, REFERRAL_STORAGE_KEY, isValidReferralCodeFormat, normalizeReferralCode } from "@/lib/referrals";
import { useAuth } from "@/lib/auth-context";
import { localePath, type TranslationKey } from "@/lib/i18n/locales";
import { useLocale, useT } from "@/lib/use-i18n";
import { DeviceCodeLogin } from "./DeviceCodeLogin";
interface AuthFormProps {
  mode: "login" | "signup";
}
function GoogleMark(t0) {
  const $ = _c(6);
  const {
    className: t1
  } = t0;
  const className = t1 === undefined ? "size-4" : t1;
  let t2;
  let t3;
  let t4;
  let t5;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />;
    t3 = <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" />;
    t4 = <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />;
    t5 = <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />;
    $[0] = t2;
    $[1] = t3;
    $[2] = t4;
    $[3] = t5;
  } else {
    t2 = $[0];
    t3 = $[1];
    t4 = $[2];
    t5 = $[3];
  }
  let t6;
  if ($[4] !== className) {
    t6 = <svg className={className} viewBox="0 0 24 24" aria-hidden="true">{t2}{t3}{t4}{t5}</svg>;
    $[4] = className;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  return t6;
}
export function AuthForm({
  mode
}: AuthFormProps) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const t = useT();
  const locale = useLocale();
  const lp = (path: string) => localePath(locale, path);
  const redirectTo = normalizeInternalPath(searchParams.get("next"));
  // 2026-09-23 programa de referidos (TAREA I, research 2026 Track360
  // doble-sided): ?ref=CODE se guarda pendiente y se reclama al obtener
  // sesión (signup email). OAuth/confirm-email lo reclama ReferralPanel
  // en el primer dashboard (ver ReferralPanel.tsx).
  const refQuery = searchParams.get("ref");
  const [referralCode, setReferralCode] = useState<string | null>(null);
  useEffect(() => {
    if (mode !== "signup" || !refQuery) return;
    if (!isValidReferralCodeFormat(refQuery)) return;
    const normalized = normalizeReferralCode(refQuery);
    // eslint-disable-next-line react-hooks/set-state-in-effect -- one-time URL capture on mount needs state sync
    setReferralCode(normalized);
    try {
      window.localStorage.setItem(REFERRAL_STORAGE_KEY, normalized);
    } catch {
      // Storage bloqueado: la nota igual se muestra (sin persistencia).
    }
  }, [mode, refQuery]);
  // 2026-09-07 mobile Google loop fix: a failed OAuth exchange now lands here
  // with ?error=auth_callback_failed (instead of a silent logged-out /chat gate),
  // so the failure is visible and one more Google tap can succeed.
  const urlError = searchParams.get("error");
  // ...plus already-authenticated users never sit on login/signup tapping
  // Google in a loop — they go straight where they were headed.
  const {
    user: authUser,
    loading: authLoading
  } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [needsEmailConfirm, setNeedsEmailConfirm] = useState(false);
  const [showDeviceCode, setShowDeviceCode] = useState(false);
  const [isNativeEnv, setIsNativeEnv] = useState(false);
  const errorId = "auth-form-error";
  const containerRef = useRef<HTMLDivElement>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const subtitleRef = useRef<HTMLParagraphElement>(null);
  const googleRef = useRef<HTMLDivElement>(null);
  const dividerRef = useRef<HTMLDivElement>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const forgotRef = useRef<HTMLDivElement>(null);
  const altRef = useRef<HTMLParagraphElement>(null);
  const errorMsgRef = useRef<HTMLDivElement>(null);
  const msgRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const hasElectron = "electronAPI" in window;
    const cap = (window as unknown as {
      Capacitor?: {
        isNativePlatform?: () => boolean;
      };
    }).Capacitor;
    const isNative = Boolean(cap?.isNativePlatform?.());
    if (hasElectron || isNative) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- one-time env detection on mount needs state sync
      setIsNativeEnv(true);
      setShowDeviceCode(true);
    }
    // If URL already carries ?code= (quick-login 8-char or OAuth PKCE), show device login
    // so user can paste/exchange without extra click — but never auto-show after normal email/password login
    const params = new URLSearchParams(window.location.search);
    const codeParam = params.get("code");
    if (codeParam && codeParam.trim().length >= 8) {
      setShowDeviceCode(true);
    }
  }, []);

  // 2026-09-07 mobile Google loop fix: already signed in → leave auth pages
  // (kills the "tap Google again on login while logged in" loop on mobile).
  useEffect(() => {
    if (!authLoading && authUser) {
      router.replace(redirectTo);
    }
  }, [authLoading, authUser, router, redirectTo]);

  // 2026-09-07 surface OAuth callback failures (?error=auth_callback_failed)
  // instead of failing silently.
  // 2026-09-21 FASE 3 FIX design-auth-admin: ?error=no_email (emitido por
  // /auth/callback cuando el proveedor no devuelve email) también necesita
  // estado visible — antes caía en silencio sin mensaje.
  useEffect(() => {
    if (urlError === "auth_callback_failed") {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- one-time URL error surfacing on mount needs state sync
      setError(t("authErrorCallbackFailed"));
    } else if (urlError === "no_email") {
      setError(t("authErrorNoEmailProvider"));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once per URL, t() is stable per locale
  }, [urlError]);

  // 2026-09-21 FASE 3 FIX design-auth-admin (WCAG 2.2 2.4.3 foco inaugural +
  // 3.3.1 foco a error): el h1 recibe foco en mount (tabIndex -1, sin scroll)
  // y el mensaje de error recibe foco cuando aparece, para que el lector de
  // pantalla lo anuncie sin buscarlo.
  useEffect(() => {
    headingRef.current?.focus({
      preventScroll: true
    });
  }, []);
  useEffect(() => {
    if (error) {
      errorMsgRef.current?.focus({
        preventScroll: false
      });
    }
  }, [error]);
  const reset = () => {
    setError(null);
    setMessage(null);
    setNeedsEmailConfirm(false);
  };

  // Reclama el ?ref= pendiente tras obtener sesión (best-effort: un fallo
  // no bloquea el registro; ReferralPanel reintenta en el dashboard).
  const claimPendingReferral = async () => {
    let pending: string | null = referralCode;
    if (!pending) {
      try {
        pending = window.localStorage.getItem(REFERRAL_STORAGE_KEY);
      } catch {
        pending = null;
      }
    }
    if (!pending || !isValidReferralCodeFormat(pending)) return;
    try {
      await fetch("/api/referrals/claim", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          code: normalizeReferralCode(pending)
        })
      });
    } catch {
      // Red transitoria: se conserva para ReferralPanel (idempotente).
      return;
    }
    try {
      window.localStorage.removeItem(REFERRAL_STORAGE_KEY);
    } catch {
      // Storage bloqueado: el próximo montaje reintentará (idempotente).
    }
  };
  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    reset();
    if (!isSupabaseConfigured()) {
      setError(t("authErrorNotActive"));
      return;
    }
    if (mode === "signup") {
      const issue = getFirstPasswordIssue(password);
      if (issue) {
        setError(passwordIssueMessage(issue, t));
        return;
      }
    }

    // Server-side attempt gate: 6 tries / 15 min per email+IP (fail-closed).
    // Supabase's hosted auth is called directly from the browser, so this
    // preflight bucket is the app-level brute-force protection.
    try {
      const gate = await fetch("/api/auth/attempt-gate", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          email,
          action: mode,
          password: mode === "signup" ? password : undefined
        })
      });
      if (gate.status === 429) {
        // 2026-08-24 overload emergency: signup blocked due to massive growth — show localized overload copy
        try {
          const j = (await gate.json()) as {
            error?: {
              message?: string;
            };
          };
          const msg = j?.error?.message;
          if (msg && /pausado|creación|temporarily paused|masivo/i.test(msg)) {
            setError(msg);
            return;
          }
        } catch {}
        setError(t("authErrorRateLimit"));
        return;
      }
    } catch {
      // Gate unreachable — fail open so a transient outage never blocks login.
    }
    setLoading(true);
    try {
      const supabase = createClient();
      if (mode === "login") {
        const {
          error: authError
        } = await supabase.auth.signInWithPassword({
          email,
          password
        });
        if (authError) {
          if (isUserBannedAuthError(authError)) {
            setError(t("authErrorBanned"));
            return;
          }
          if (/email not confirmed/i.test(authError.message)) {
            setNeedsEmailConfirm(true);
          }
          setError(translateAuthError(authError.message, t));
          return;
        }
        router.push(redirectTo);
        router.refresh();
        return;
      }

      // Product kill switch / feature flag for signup (public via /api/banner).
      // one-shot submit-time: frescura justificada (kill-switch signup), exento de regla interval-cache.
      try {
        const flagRes = await fetch("/api/banner");
        if (flagRes.ok) {
          const flags = (await flagRes.json()) as {
            signupEnabled?: boolean;
          };
          if (flags.signupEnabled === false) {
            setError(t("authErrorSignupDisabled"));
            return;
          }
        }
      } catch {
        // Banner probe failed — fail open so a transient outage does not block signup.
      }
      const {
        data,
        error: authError
      } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/auth/callback?next=${encodeURIComponent(redirectTo)}`
        }
      });
      if (authError) {
        // Lote Fase 5 anti-enumeración: "ya registrado" responde igual que un
        // registro nuevo (ver arriba); el resto de errores sí se muestran.
        if (/user already registered/i.test(authError.message)) {
          setNeedsEmailConfirm(true);
          setMessage(t("authConfirmSent", {
            email: CONTACT_EMAIL
          }));
          return;
        }
        setError(translateAuthError(authError.message, t));
        return;
      }
      if (data.session) {
        await claimPendingReferral();
        // 2026-09-23 embudo signup (SSOT src/lib/conversion-funnel.ts;
        // research 2026 ChartMogul/OpenView: signup→activación→pago, el MIN
        // por usuario deriva el primero downstream). Best-effort vía la
        // telemetría existente /api/chat/events (allowlist funnel_signup);
        // sin PII (solo source, nunca email). Nunca bloquea el redirect.
        try {
          void fetch("/api/chat/events", {
            method: "POST",
            headers: {
              "Content-Type": "application/json"
            },
            body: JSON.stringify({
              event: "funnel_signup",
              metadata: {
                source: "email-signup"
              }
            }),
            keepalive: true
          }).catch(() => undefined);
        } catch {
          // Telemetría fail-open: un fallo nunca bloquea el registro.
        }
        router.push(redirectTo);
        router.refresh();
        return;
      }

      // Supabase anti-enumeration: si el email ya existe, el signup devuelve 200
      // sin sesión y SIN enviar correo. La respuesta es IDÉNTICA a un registro
      // nuevo (lote Fase 5 auth): revelar "ya registrado" permitiría enumerar
      // cuentas. Quien ya tiene cuenta simplemente no recibe correo nuevo.
      if (isLikelyExistingAccountSignUp(data)) {
        setNeedsEmailConfirm(true);
        setMessage(t("authConfirmSent", {
          email: CONTACT_EMAIL
        }));
        return;
      }
      setNeedsEmailConfirm(true);
      setMessage(t("authConfirmSent", {
        email: CONTACT_EMAIL
      }));
    } catch {
      setError(t("authErrorGeneric"));
    } finally {
      setLoading(false);
    }
  };
  const handleResendConfirm = async () => {
    reset();
    if (!email.trim()) {
      setError(t("authErrorNoEmail"));
      return;
    }
    if (!isSupabaseConfigured()) {
      setError(t("authErrorNotActive"));
      return;
    }
    try {
      // one-shot submit-time: frescura justificada (kill-switch signup), exento de regla interval-cache.
      const flagRes = await fetch("/api/banner");
      if (flagRes.ok) {
        const flags = (await flagRes.json()) as {
          signupEnabled?: boolean;
        };
        if (flags.signupEnabled === false) {
          setError(t("authErrorSignupDisabled"));
          return;
        }
      }
    } catch {
      // Fail open — DB trigger is the hard gate for new signups.
    }
    setLoading(true);
    try {
      const supabase = createClient();
      const {
        error: resendError
      } = await supabase.auth.resend({
        type: "signup",
        email: email.trim(),
        options: {
          emailRedirectTo: `${window.location.origin}/auth/callback?next=${encodeURIComponent(redirectTo)}`
        }
      });
      if (resendError) {
        if (/rate limit/i.test(resendError.message)) {
          setError(t("authErrorRateLimit"));
          return;
        }
        setError(translateAuthError(resendError.message, t));
        return;
      }
      setNeedsEmailConfirm(true);
      setMessage(t("authResendConfirmSent"));
    } catch {
      setError(t("authErrorGeneric"));
    } finally {
      setLoading(false);
    }
  };
  const handleGoogle = async () => {
    reset();
    if (!isSupabaseConfigured()) {
      setError(t("authErrorGoogleNotActive"));
      return;
    }
    // Signup-mode Google must respect the same kill/feature as email signup.
    if (mode === "signup") {
      try {
        // one-shot submit-time: frescura justificada (kill-switch signup), exento de regla interval-cache.
        const flagRes = await fetch("/api/banner");
        if (flagRes.ok) {
          const flags = (await flagRes.json()) as {
            signupEnabled?: boolean;
          };
          if (flags.signupEnabled === false) {
            setError(t("authErrorSignupDisabled"));
            return;
          }
        }
      } catch {
        // Fail open — same policy as email signup path.
      }
    }
    setLoading(true);
    try {
      // Mark OAuth start for AuthCodeCopyModal suppress logic (avoid auto-popup on non-OAuth ?code)
      try {
        sessionStorage.setItem("vortixy:oauth:pending", String(Date.now()));
      } catch {}
      const supabase = createClient();
      const isDesktop = typeof window !== "undefined" && "electronAPI" in window;
      // Native mobile (Capacitor): Google blocks OAuth inside embedded WebViews
      // ("disallowed_useragent") and the user's Google accounts live in their
      // default browser — so open the flow there (Chrome Custom Tabs via
      // @capacitor/browser) and return through the vortixy:// deep link, the
      // exact same pattern the Electron desktop app already uses.
      const isNative = typeof window !== "undefined" && Boolean((window as {
        Capacitor?: {
          isNativePlatform?: () => boolean;
        };
      }).Capacitor?.isNativePlatform?.());
      const deepLinkRedirect = `vortixy://auth/callback?next=${encodeURIComponent(redirectTo)}`;
      if (!isDesktop && isNative) {
        const {
          data,
          error
        } = await supabase.auth.signInWithOAuth({
          provider: "google",
          options: {
            redirectTo: deepLinkRedirect,
            // The WebView must NOT navigate to Google itself (it would be
            // blocked); we open the authorization URL in the system browser.
            skipBrowserRedirect: true
          }
        });
        if (error) {
          setError(translateAuthError(error.message, t));
        } else if (data?.url) {
          // 2026-08-30 UX fix: give feedback so user knows to complete in system browser
          // (prevents "no pasa nada" perception while Chrome Custom Tab is open).
          setMessage(t("authGoogleContinueInBrowser") ?? "Continúa en tu navegador para completar el inicio de sesión…");
          try {
            const {
              Browser
            } = await import("@capacitor/browser");
            await Browser.open({
              url: data.url
            });
          } catch {
            // If the native browser plugin is unavailable, fall back to the
            // plain redirect (still better than a stuck loading state).
            window.location.href = data.url;
          }
        }
        return;
      }

      // 2026-08-23 desktop OAuth fix — verified 2026-05-28 OpenReplay "Add Authentication to an Electron App":
      // RFC 8252 forbids embedded BrowserWindow for OAuth (Google 403 disallowed_useragent).
      // Must use system browser + PKCE + deep link. Previous code relied on will-navigate
      // interception without skipBrowserRedirect, which races and leaves the PKCE verifier
      // in the wrong partition. Now desktop uses the same skipBrowserRedirect pattern as
      // native: get data.url via PKCE (verifier stored in persist:vortixy partition) and
      // explicitly open in system browser via electronAPI.openExternal. The system browser
      // then redirects to vortixy://auth/callback?code=... which the OS delivers to Electron
      // via second-instance / open-url and handleDeepLink loads https://www.vortixy.net/auth/callback
      // in the app partition to exchange code for session. This bridges the browser ↔ app
      // cookie-jar boundary (2026-04-08 Clerk+Next.js16 desktop handoff pattern).
      if (isDesktop) {
        const {
          data,
          error
        } = await supabase.auth.signInWithOAuth({
          provider: "google",
          options: {
            redirectTo: deepLinkRedirect,
            skipBrowserRedirect: true
          }
        });
        if (error) {
          setError(translateAuthError(error.message, t));
          return;
        }
        if (data?.url) {
          // 2026-08-30 UX + reliability fix: tell user to complete in system browser
          // and keep the Electron window responsive. The OS will deliver vortixy://
          // back to main.js handleDeepLink which loads /auth/callback in the app partition.
          setMessage(t("authGoogleContinueInBrowser") ?? "Se abrió tu navegador — completa el inicio de sesión allí y volverás automáticamente a la app.");
          try {
            const electronAPI = (window as unknown as {
              electronAPI?: {
                openExternal?: (u: string) => void;
              };
            }).electronAPI;
            if (electronAPI?.openExternal) {
              electronAPI.openExternal(data.url);
            } else {
              window.location.href = data.url;
            }
          } catch {
            window.location.href = data.url;
          }
        }
        return;
      }
      const {
        error: authError
      } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: {
          redirectTo: `${window.location.origin}/auth/callback?next=${encodeURIComponent(redirectTo)}`
        }
      });
      if (authError) {
        setError(translateAuthError(authError.message, t));
      }
    } catch {
      setError(t("authErrorGeneric"));
    } finally {
      setLoading(false);
    }
  };
  const handleReset = async () => {
    reset();
    if (!email.trim()) {
      setError(t("authErrorNoEmail"));
      return;
    }
    if (!isSupabaseConfigured()) {
      setError(t("authErrorRecoveryNotActive"));
      return;
    }
    // Lote Fase 5: el reset también pasa por attempt-gate (action "reset").
    try {
      const gate = await fetch("/api/auth/attempt-gate", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          email,
          action: "reset"
        })
      });
      if (gate.status === 429) {
        setError(t("authErrorRateLimit"));
        return;
      }
    } catch {
      // Gate inalcanzable — fail open como en login/signup.
    }
    setLoading(true);
    try {
      const supabase = createClient();
      const {
        error: resetError
      } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: buildPasswordRecoveryRedirectTo(window.location.origin, redirectTo)
      });
      if (resetError) {
        // Lote Fase 5 anti-enumeración: rate-limit e email inválido se
        // muestran; cualquier otro fallo responde como éxito genérico.
        if (/rate limit/i.test(resetError.message)) {
          setError(t("authErrorRateLimit"));
          return;
        }
        if (/invalid email|validate email/i.test(resetError.message)) {
          setError(t("authErrorInvalidEmail"));
          return;
        }
        setMessage(t("authRecoverySent", {
          email: CONTACT_EMAIL
        }));
        return;
      }
      setMessage(t("authRecoverySent", {
        email: CONTACT_EMAIL
      }));
    } catch {
      setError(t("authErrorGeneric"));
    } finally {
      setLoading(false);
    }
  };
  const isLogin = mode === "login";
  const showGoogleMailNudge = isGoogleMailAddress(email);
  const altQuery = redirectTo !== "/chat" ? `?next=${encodeURIComponent(redirectTo)}` : "";
  const altHref = isLogin ? `${lp("/signup")}${altQuery}` : `${lp("/login")}${altQuery}`;
  return <div ref={containerRef} className="w-full page-enter">
      {/* 2026-09-10 migración Lovable-hero sobrio: form envuelto en
          lovable-card (mismo patrón que ContactContent) — max-w 460px del
          AuthLayout intacto, top bar intacta. */}
      {/* 2026-09-26 sin scroll: p-6/sm:p-7 → p-5/sm:p-5 para que login +
          signup quepan en un solo frame 100dvh sin scroll. */}
      <div className="auth-card w-full p-5 sm:p-6">
        {/* Title */}
        <div className="mb-4 text-start">
          {/* 2026-09-26 FIX título gigante: text-display (step-5, 40→80px,
              2 líneas enormes en la captura) → serif compacto 26/30px.
              Mantiene serif editorial + balance, pero cabe en 1-2 líneas
              sin devorar el frame. tabIndex -1 intacto (WCAG 2.2 2.4.3). */}
          {/* 2026-10-02 fix chat · a11y + defecto visual: el anillo de foco se
              pintaba alrededor del h1 en /login y /signup en CADA carga (con
              teclado y con ratón). El foco aquí es intencionado (WCAG 2.2
              2.4.3: el lector anuncia el título al entrar); lo que no debe es
              dibujar anillo. `outline-none` a secas no servía: la utilidad
              vive en `@layer utilities` (Tailwind v4) y el `:focus-visible`
              global de src/app/globals.css está SIN capa; una declaración
              unlayered gana a CUALQUIER declaración de una capa con la misma
              especificidad (CSS Cascade 5 §6.4), así que la utilidad era
              inerte. Medido en navegador (Chromium 153 + Firefox 155) con el
              CSS compilado: `outline-style: solid 2px rgb(234,234,244)` + halo
              `box-shadow 4px`. Para declaraciones `!important` el orden de
              capas se INVIERTE, de modo que la utilidad importante sí gana —
              mismo criterio que /banned (outline-none), ahora operativo.
              `shadow-none!` cierra además el halo de 4px del global. */}
          <h1 ref={headingRef} tabIndex={-1}
        // 2026-10-03 RUN12 FASE 8 N3 [auth-h1-step-2] (research 2026 Utopia,
        // reutiliza auditoría F8: text-[26px]/sm:text-[30px] → step-2 único
        // 23.30/26.32/31.30px, Δ−2.70/−3.68/+1.30 dentro del delta 2-4px
        // aceptado; el par base/sm literal (step-1/step-2) daba −6.5px en
        // 360 y se descartó. tracking-tight + leading-[1.1] intactos,
        // tabIndex −1 intacto (WCAG 2.2 2.4.3)).
        className="font-display text-[27px] font-semibold leading-[1.1] tracking-tight text-foreground text-balance sm:text-[31px] outline-none! shadow-none!">
            {isLogin ? t("authLogin") : t("authSignup")}
          </h1>
          <p ref={subtitleRef} className="mt-1 text-[13px] text-muted-foreground">
            {isLogin ? t("authLoginSubtitle") : t("authSignupSubtitle")}
          </p>
          {/* 2026-09-23 programa de referidos: nota de invitado con la
              recompensa (doble-sided {days}d Pro) — 1 línea quiet. */}
          {!isLogin && referralCode ? <div role="status" className="mt-3 flex items-start gap-2 rounded-xl bg-[var(--success)]/10 border border-[var(--success)]/25 p-3 text-[13px] text-[var(--success-text)]">
              <CheckCircle2 className="size-4 shrink-0 mt-0.5" aria-hidden="true" />
              <span className="min-w-0 flex-1">
                {t("referralSignupNote", {
              days: REFERRAL_REWARD_DAYS
            })}
              </span>
            </div> : null}
        </div>

        {/* OAuth Buttons */}
        <div ref={googleRef} className="space-y-2.5">
          <button type="button" onClick={handleGoogle} disabled={loading} className="flex h-11 w-full cursor-pointer items-center justify-center gap-3 rounded-[var(--radius-button)] border border-border bg-[var(--background)] text-[14px] font-medium text-foreground transition-[background-color,border-color] hover:border-border-strong hover:bg-muted active:scale-[0.99] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)]" aria-busy={loading}>
            {loading ? <Loader2 className="size-5 animate-spin motion-reduce:animate-none text-muted-foreground" aria-hidden="true" /> : <GoogleMark className="size-5" />}
            <span>{t("authGoogle")}</span>
          </button>
        </div>

        {/* Divider — 2026-09-26 sin scroll: my-5 → my-3. */}
        <div ref={dividerRef} className="my-3 flex items-center gap-3">
          <div className="h-px flex-1 bg-border" />
          <span className="text-[12px] font-normal lowercase text-muted-foreground">
            {t("authEmail")}
          </span>
          <div className="h-px flex-1 bg-border" />
        </div>

        {/* Form Inputs — 2026-09-26 sin scroll: space-y-4 → space-y-3. */}
        <form ref={formRef} onSubmit={handleSubmit} className="space-y-3">
          <div className="space-y-1.5">
            <label htmlFor="auth-email" className="block text-[13px] font-medium text-foreground/80">
              {t("authEmailLabel")}
            </label>
            <div className="relative">
              {/* 2026-09-24 FASE 3 FIX design-docs-landing (research 2026 F6 SSOT
                  radius: fields usan --radius-field 16px, el pill vive solo en
                  botones — este Input pisaba el radio base del sistema con pill;
                  sin cambio visual en desktop, bordes coherentes en 360px). */}
              <Input id="auth-email" name="email" type="email" autoComplete="email" required placeholder={t("authEmailPlaceholder")} value={email} onChange={event => setEmail(event.target.value)} error={!!error}
            // 2026-09-26 FASE 4 MEJORA transversal M-aria (research 2026
            // adacompliancepros 2026-05-21 Accessible Error Messages WCAG 2.2
            // + W3C ARIA21 paridad DeviceCodeLogin/ContactForm: omitir el
            // atributo sin error en vez de aria-invalid="false" — "false"
            // es ruido para AT. Sin cambio visual.)
            aria-invalid={error ? true : undefined} aria-describedby={error ? errorId : showGoogleMailNudge ? "auth-gmail-nudge" : undefined} className="h-11 rounded-[var(--radius-field)] border-border bg-[var(--background)] px-4 text-[14px] text-foreground placeholder:text-muted-foreground" />
            </div>
          </div>

          {showGoogleMailNudge ? <div id="auth-gmail-nudge" role="status" className="flex flex-col gap-2.5 rounded-[var(--radius-card)] border border-border bg-muted/20 p-3.5 text-foreground">
              <div className="flex items-start gap-3">
                <span className="mt-0.5 inline-flex size-7 shrink-0 items-center justify-center rounded-lg bg-muted">
                  <GoogleMark className="size-4" />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-[13px] font-semibold text-foreground">
                    {t("authGmailNudgeTitle")}
                  </p>
                  <p className="text-[12px] text-muted-foreground">
                    {isLogin ? t("authGmailNudgeLogin") : t("authGmailNudgeSignup")}
                  </p>
                </div>
              </div>
              <button type="button" onClick={() => void handleGoogle()} disabled={loading}
          // 2026-09-23 FASE 3 FIX diseño (research 2026 WCAG 2.2 2.5.8 + Apple HIG 44pt:
          // h-9 36px → h-11 44px, paridad botón primario del sistema).
          className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-[var(--radius-button)] border border-border bg-muted text-[13px] font-medium text-foreground transition-colors hover:bg-background-soft focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)] focus-visible:ring-offset-2" aria-busy={loading}>
                {loading ? <Loader2 className="size-4 animate-spin motion-reduce:animate-none text-muted-foreground" aria-hidden="true" /> : <GoogleMark className="size-4" />}
                {t("authGmailNudgeCta")}
              </button>
            </div> : null}

          <div className="space-y-1.5">
            {/* MINIMALISTA PRO: forgot + mostrar/ocultar agrupados en una sola
                fila de utilidades (misma función, mismos handlers/aria) */}
            {/* 2026-09-12 responsive: flex-wrap evita colisión label+botones en 360px */}
            <div ref={forgotRef} className="flex items-center justify-between gap-2">
              <label htmlFor="auth-password" className="block text-[13px] font-medium text-foreground/80">
                {t("authPassword")}
              </label>
              {isLogin && <button type="button" onClick={handleReset} disabled={loading} className="rounded-sm min-h-11 inline-flex items-center px-1 text-xs text-foreground-soft transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)] focus-visible:ring-offset-2">
                  {t("authForgotPassword")}
                </button>}
            </div>
            <div className="relative">
              <Input id="auth-password" name="password" type={showPassword ? "text" : "password"} autoComplete={isLogin ? "current-password" : "new-password"} minLength={isLogin ? undefined : 8} required placeholder={isLogin ? t("authPasswordPlaceholder") : t("authPasswordMinPlaceholder")} value={password} onChange={event => setPassword(event.target.value)} error={!!error}
            // 2026-09-26 FASE 4 MEJORA transversal M-aria (misma nota que
            // auth-email arriba: ? true : undefined, sin "false").
            aria-invalid={error ? true : undefined} aria-describedby={error ? errorId : !isLogin ? "auth-password-hint" : undefined} className="h-11 rounded-[var(--radius-field)] border-border bg-[var(--background)] ps-4 pe-11 text-[14px] text-foreground placeholder:text-muted-foreground" />
              <button type="button" onClick={() => setShowPassword(visible => !visible)} className="absolute end-0 top-0 bottom-0 flex min-h-11 min-w-11 items-center justify-center rounded-e-[var(--radius-field)] text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)] focus-visible:ring-offset-2" aria-label={showPassword ? t("authHidePassword") : t("authShowPassword")} aria-pressed={showPassword}>
                {showPassword ? <EyeOff className="size-4 shrink-0" aria-hidden="true" /> : <Eye className="size-4 shrink-0" aria-hidden="true" />}
                <span className="sr-only">
                  {showPassword ? t("authHidePassword") : t("authShowPassword")}
                </span>
              </button>
            </div>
          </div>

          {!isLogin && <p id="auth-password-hint" className="text-[12px] text-muted-foreground">
              {t("authPasswordHint")}
            </p>}

          {/* 2026-09-28 D4-03: la región viva estaba sobre un `<ul>` que se
              MONTABA condicionalmente junto con su contenido ⇒ un live region
              que nace ya poblado no la anuncian los lectores de pantalla
              (WCAG 2.2 4.1.3 Status Messages: la región debe existir ANTES del
              cambio de texto). research 2026-09-28 WAI-ARIA status role + MDN
              aria-live. El envoltorio no lleva caja (sin borde/padding/margin)
              ⇒ 0 píxeles y 0 CLS; el `<ul>` conserva su superficie intacta.
              Distinto del `role="alert"` de más abajo, que SÍ se anuncia al
              insertarse y por eso no se toca. */}
          <div aria-live="polite" aria-atomic="false">
            {!isLogin && password.length > 0 && !getPasswordStrength(password).ok && !error && <ul className="space-y-1 rounded-[var(--radius-card)] border border-border bg-muted/20 p-3 text-[12px] text-muted-foreground">
                  {getPasswordStrength(password).issues.map(issue => <li key={issue} className="flex items-start gap-1.5">
                      <span className="mt-1.5 size-1 shrink-0 rounded-full bg-[var(--warning)]" aria-hidden="true" />
                      <span>{passwordIssueMessage(issue, t)}</span>
                    </li>)}
                </ul>}
          </div>

          {error && <div ref={errorMsgRef} key={error} id={errorId} role="alert" tabIndex={-1} className="flex items-start gap-2 rounded-xl bg-[var(--danger)]/12 border border-[var(--danger)]/30 p-3 text-[13px] text-[var(--danger-text)] v-rise outline-none">
              <AlertCircle className="size-4 shrink-0 mt-0.5" aria-hidden="true" />
              <span className="min-w-0 flex-1">
                <span>{error}</span>
                {/* MINIMALISTA PRO: reenviar vive dentro del mensaje de estado */}
                {needsEmailConfirm && <button type="button" onClick={() => void handleResendConfirm()} disabled={loading}
            // 2026-09-24 FASE 3 FIX design-auth-tokens (research 2026
            // WCAG 2.2 2.5.8: target ≥24px — min-h-6).
            // 2026-09-28 D4-15: `text-left` es FÍSICO — en `ar` el
            // enlace quedaba a la izquierda mientras el mensaje se
            // alineaba a la derecha ⇒ `text-start` (propiedad lógica).
            // Y `min-h-6` = exactamente 24px, al suelo y sin `min-w`,
            // cuando los controles hermanos (:800/:808) usan
            // `min-h-11 min-w-11`: research 2026-09-28 WCAG 2.2 SC
            // 2.5.8 Target Size (Minimum) + Apple HIG 44pt táctil.
            className="mt-1 block min-h-11 w-full text-start text-[13px] underline transition-colors hover:text-foreground">
                    {t("authResendConfirm")}
                  </button>}
              </span>
            </div>}
          {message && !error && <div ref={msgRef} key={message} role="status" className="flex items-start gap-2 rounded-xl bg-[var(--success)]/10 border border-[var(--success)]/25 p-3 text-[13px] text-[var(--success-text)] v-rise">
              <CheckCircle2 className="size-4 shrink-0 mt-0.5" aria-hidden="true" />
              <span className="min-w-0 flex-1">
                <span>{message}</span>
                {/* MINIMALISTA PRO: reenviar vive dentro del mensaje de estado */}
                {needsEmailConfirm && <button type="button" onClick={() => void handleResendConfirm()} disabled={loading}
            // 2026-09-24 FASE 3 FIX design-auth-tokens (research 2026
            // WCAG 2.2 2.5.8: target ≥24px — min-h-6).
            // 2026-09-28 D4-15: ver el gemelo del panel `role="alert"`
            // de arriba — `text-start` (lógico) + `min-h-11` (paridad
            // `min-h-11 min-w-11` de :800/:808).
            className="mt-1 block min-h-11 w-full text-start text-[13px] underline transition-colors hover:text-foreground">
                    {t("authResendConfirm")}
                  </button>}
              </span>
            </div>}

          <Button variant="accent" className="h-11 w-full cursor-pointer" type="submit" disabled={loading} aria-busy={loading}>
            {loading ? <Loader2 className="size-5 animate-spin motion-reduce:animate-none text-accent-foreground" aria-hidden="true" /> : isLogin ? t("authLogin") : t("authSignup")}
          </Button>
        </form>

        {/* ——— Device code fallback — minimalista pro: tertiary, 1 border + soft shadow ——— */}
        {/* 2026-09-21 FASE 3 FIX design-auth-admin: disclosure real (aria-expanded
            + aria-controls) colapsado por defecto — con el panel abierto hay un
            solo submit primario (el del email); el del código es secundario
            (ver DeviceCodeLogin variant outline) y el panel colapsa a 360px. */}
        {showDeviceCode ? <div className="mt-3" id="auth-device-code">
            <DeviceCodeLogin />
            {!isNativeEnv ? <button type="button" onClick={() => setShowDeviceCode(false)} aria-expanded={showDeviceCode} aria-controls="auth-device-code"
        // 2026-09-23 FASE 3 FIX diseño (research 2026 WCAG 2.2 1.4.3 AA:
        // 11px atenuado no llega a AA — sube a 12px + fg pleno).
        // 2026-09-24 FASE 3 Lovable (research 2026 W3C WCAG 2.2 §2.5.8
        // https://www.w3.org/WAI/WCAG22/Understanding/target-size-minimum.html:
        // target táctil min-h-11 — el toggle del fallback no puede ser
        // una línea de texto de ~16px).
        className="mt-2 inline-flex min-h-11 w-full items-center justify-center text-center text-xs font-medium tracking-wide text-muted-foreground hover:text-foreground transition-colors">
                {t("authDeviceCodeHide") ?? "Ocultar"}
              </button> : null}
          </div> : <button type="button" onClick={() => setShowDeviceCode(true)} aria-expanded={showDeviceCode}
      // 2026-09-23 FASE 3 FIXER diseño Auth-H7 (research 2026 WCAG 2.2
      // 1.3.1 + APG disclosure: aria-controls huérfano — el panel
      // #auth-device-code no existe colapsado; sin controls hasta
      // expandir, donde el botón ocultar sí lo referencia).
      // 2026-09-24 FASE 3 FIX design-auth-tokens: transition-all →
      // background/border/color (sin repintar layout).
      className="mt-3 inline-flex w-full min-h-10 items-center justify-center gap-1.5 rounded-full border border-border/60 bg-[var(--background)] px-3 py-1.5 text-xs font-medium tracking-wide text-muted-foreground shadow-xs transition-[background-color,border-color,color] hover:bg-muted/40 hover:text-foreground hover:border-border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)] focus-visible:ring-offset-2" aria-label={t("authDeviceCodeShow") ?? "¿Inicias desde otro dispositivo?"}>
            <KeyRound className="size-3.5 opacity-60" aria-hidden="true" />
            {t("authDeviceCodeShowAlt") ?? "¿Inicias desde otro dispositivo?"}
          </button>}
      </div>

      {/* Switch Mode Link — 2026-09-26 sin scroll: mt-6 → mt-3. */}
      <p ref={altRef} className="mt-3 text-center text-[13px] text-muted-foreground">
        {isLogin ? t("authNoAccount") : t("authHasAccount")}{" "}
        <Link href={altHref} className="font-medium text-foreground transition-[text-decoration-color] hover:underline">
          {isLogin ? t("authCreateAccount") : t("authLogin")}
        </Link>
      </p>
    </div>;
}
function passwordIssueMessage(issue: PasswordStrengthIssue, t: (key: TranslationKey) => string): string {
  switch (issue) {
    case "too_short":
      return t("authErrorPasswordLength");
    case "missing_upper":
      return t("authErrorPasswordMissingUpper");
    case "missing_lower":
      return t("authErrorPasswordMissingLower");
    case "missing_digit":
      return t("authErrorPasswordMissingDigit");
  }
}
function translateAuthError(message: string, t: (key: TranslationKey) => string): string {
  if (/invalid login credentials/i.test(message)) {
    return t("authErrorInvalidCredentials");
  }
  if (/email not confirmed/i.test(message)) {
    return t("authErrorEmailNotConfirmed");
  }
  if (/user already registered/i.test(message)) {
    return t("authErrorAlreadyRegistered");
  }
  if (/password should be at least/i.test(message) || /password.*too short/i.test(message)) {
    return t("authErrorPasswordLength");
  }
  if (/password.*upper/i.test(message) || /password.*lower/i.test(message) || /password.*digit/i.test(message) || /password.*number/i.test(message) || /weak.*password/i.test(message) || /not strong enough/i.test(message)) {
    return t("authErrorPasswordRules");
  }
  if (/rate limit/i.test(message)) {
    return t("authErrorRateLimit");
  }
  return t("authErrorGeneric");
}